import { copy } from '@thd-olt-functional/utils';
import { HD_URL, SCHEMA_CTX } from '../constants';
import { getAggregateReviews } from '../helpers/product-helpers';

function getDimension(suppDimensions, entryGroup, dimension) {
  const entry = (suppDimensions || []).filter(
    ({ specTitle }) => specTitle === entryGroup,
  );
  let spec;
  if (entry.length) {
    spec = (entry[0].specifications || [])
      .sort((specA, specB) => {
        if (specA.specName < specB.specName) {
          return -1;
        }
        if (specA.specName > specB.specName) {
          return 1;
        }
        return 0;
      })
      .find(({ specName = '' }) => specName.toLowerCase().includes(dimension)) || {};
  }
  return spec?.specValue || '';
}

function checkEnergyConsumption(specs) {
  const certification = getDimension(
    specs,
    'Warranty / Certifications',
    'certifications and listings',
  ).split(',');
  return certification.indexOf('Energy Star') > -1;
}

function generateReturnablePolicy(returnable) {
  const template = {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: 'US',
  };
  const regex = /\d+/;
  const regexResult = regex.exec(returnable);

  if (regexResult) {
    return {
      ...template,
      returnPolicyCategory: `${SCHEMA_CTX}/MerchantReturnFiniteReturnWindow`,
      merchantReturnDays: Number(regexResult[0]),
    };
  }

  return {
    ...template,
    returnPolicyCategory: `${SCHEMA_CTX}/MerchantReturnNotPermitted`,
  };
}

function buildBaseProductSchema({
  schemaExclude,
  productLabel,
  media,
  description,
  itemId,
  storeSkuNumber,
  upcGtin13,
  modelNumber,
  specs,
  brandName,
}) {
  const isExcluded = (key) => schemaExclude?.includes(key);

  return {
    name: isExcluded('name') ? undefined : productLabel,
    image: isExcluded('image')
      ? undefined
      : media?.images?.map(({ url }) => url.replace('<SIZE>', '100')),
    description: isExcluded('description') ? undefined : description,
    productID: isExcluded('productID') ? undefined : itemId,
    sku: isExcluded('sku') ? undefined : storeSkuNumber,
    gtin13: isExcluded('gtin13') ? undefined : upcGtin13,
    model: isExcluded('model') || !modelNumber ? undefined : modelNumber,
    depth: isExcluded('depth')
      ? undefined
      : getDimension(specs, 'Dimensions', 'depth'),
    height: isExcluded('height')
      ? undefined
      : getDimension(specs, 'Dimensions', 'height'),
    width: isExcluded('width')
      ? undefined
      : getDimension(specs, 'Dimensions', 'width'),
    color: isExcluded('color')
      ? undefined
      : getDimension(specs, 'Details', 'color'),
    weight: isExcluded('weight')
      ? undefined
      : getDimension(specs, 'Details', 'weight'),
    brand:
      isExcluded('brand') || !brandName
        ? undefined
        : {
          '@type': 'Brand',
          name: brandName,
        },
  };
}

function buildOffer({
  original,
  value,
  effectiveMap,
  promotion,
  canonicalUrl,
  availabilityType,
  returnable,
}) {
  const finalPrice = effectiveMap || value || original;
  if (!finalPrice) return undefined;
  const hasStrikeThroughPrice = original && finalPrice !== original;
  const today = new Date(Date.now());
  return {
    '@type': 'Offer',
    url: `${HD_URL}${canonicalUrl}`,
    priceCurrency: 'USD',
    price: finalPrice,
    priceValidUntil:
      promotion?.dates?.end
      || `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`,
    ...(hasStrikeThroughPrice && {
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        priceType: `${SCHEMA_CTX}/StrikethroughPrice`,
        price: original,
        priceCurrency: 'USD',
      },
    }),
    availability: availabilityType?.status
      ? `${SCHEMA_CTX}/InStock`
      : undefined,
    hasMerchantReturnPolicy: generateReturnablePolicy(returnable),
  };
}

/**
 * Builds a structured data object for a product following Schema.org Product format.
 *
 * @param {Object} params - The parameters object
 * @param {Object} [params.reviewsData] - Review data containing aggregate ratings and individual reviews
 * @param {Object} [params.availabilityType] - Product availability status information
 * @param {Object} [params.identifiers={}] - Product identifiers (itemId, brandName, upcGtin13, etc.)
 * @param {string} [params.productLabel=""] - The product name/label
 * @param {Object} [params.media] - Media object containing product images
 * @param {string} [params.productDescription=""] - Product description text
 * @param {Object} [params.pricing={}] - Pricing information (value, original, promotion, mapDetail)
 * @param {Array} [params.specifications=[]] - Product specifications/dimensions
 * @param {Array} [params.schemaExclude] - Array of field names to exclude from schema
 * @param {boolean} [params.ppq] - Flag to indicate if reviews should be excluded
 * @param {string} [params.returnable] - Return policy information
 * @param {boolean} [params.hideIfHDPPSku] - Flag to hide reviews if SKU is classified as HDPP
 * @returns {Object} A structured data object conforming to Schema.org Product schema
 */
export const getProductStructuredData = ({
  reviewsData,
  availabilityType,
  identifiers = {},
  productLabel = '',
  media,
  productDescription = '',
  pricing = {},
  specifications = [],
  schemaExclude,
  ppq,
  returnable,
  hideIfHDPPSku,
  excludeReviews = false,
}) => {
  const {
    itemId,
    brandName = '',
    upcGtin13 = '',
    storeSkuNumber = '',
    canonicalUrl = '',
    skuClassification = '',
    modelNumber,
  } = identifiers;
  const { value, promotion, original, mapDetail } = pricing || {};
  const { effectiveMap } = mapDetail || {};

  const aggregateReviews = getAggregateReviews(reviewsData || {});
  const shouldIncludeReviews = !excludeReviews && !(hideIfHDPPSku && skuClassification === 'hdpp');

  const specs = copy(specifications).data;
  const hasAggregateReviews = Object.keys(aggregateReviews).length > 0;

  const reviews = reviewsData?.Results || [];

  return {
    '@context': SCHEMA_CTX,
    '@type': 'Product',
    ...buildBaseProductSchema({
      schemaExclude,
      productLabel,
      media,
      description: productDescription,
      itemId,
      storeSkuNumber,
      upcGtin13,
      modelNumber,
      specs,
      brandName,
    }),
    ...(checkEnergyConsumption(specs) && {
      hasCertification: {
        '@type': 'Certification',
        issuedBy: {
          '@type': 'Organization',
          name: 'Energy Star',
        },
      },
    }),
    ...(shouldIncludeReviews
      && hasAggregateReviews && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: aggregateReviews?.averageRating,
        reviewCount: aggregateReviews?.totalReviews,
      },
    }),
    offers: schemaExclude?.includes('offers')
      ? undefined
      : buildOffer({
        original,
        value,
        effectiveMap,
        promotion,
        canonicalUrl,
        availabilityType,
        returnable,
      }),
    ...(shouldIncludeReviews
      && !ppq && {
      review: schemaExclude?.includes('review')
        ? undefined
        : reviews.map((review) => ({
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.Rating,
            bestRating: '5',
          },
          author: {
            '@type': 'Person',
            name: review.UserNickname,
          },
          headline: review.Title,
          reviewBody: review.ReviewText,
        })),
    }),
  };
};
